import React, { useState, useRef, useEffect } from "react";
import MaterialTable from "material-table";
import { ThemeProvider } from "@mui/material/styles";
import {
  InputAdornment,
  TextField,
  IconButton,
  Typography,
  Grid,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Fade,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import SearchIcon from "@mui/icons-material/Search";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import CsvIcon from "@mui/icons-material/Description";
import moment from "moment/min/moment-with-locales";
import theme from "styles/tableStyle";
import localization from "../components/Table/Localization";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";

const TransactionTable = ({ data, isRTL, settings }) => {
  const { t, i18n } = useTranslation();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const tableRef = useRef(null);

  // Set moment locale based on the current language
  useEffect(() => {
    // Set moment locale to match the current i18n language
    moment.locale(i18n.language);
  }, [i18n.language]);

  // Export menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Format date with the current locale
  const formatDate = (date) => {
    return date ? moment(date).format("D MMM YYYY - h:mm a") : null;
  };

  // Export functions
  const exportData = (format) => {
    handleClose();

    if (format === "csv") {
      // CSV export logic
      const csvContent = [
        columns.map((col) => col.title).join(","),
        ...filteredData.map((row) =>
          columns
            .map((col) => {
              if (col.field === "date") {
                return formatDate(row[col.field]);
              }
              if (col.field === "type") {
                return t(row[col.field]);
              }
              if (col.field === "amount") {
                return settings.swipe_symbol
                  ? row[col.field] + " " + settings.symbol
                  : settings.symbol + " " + row[col.field];
              }
              return row[col.field] || "";
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "transaction_history.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Generic export logic (could be Excel, PDF, etc.)
      console.log("Exporting in format:", format);
      // Implement other export formats as needed
    }
  };

  // Custom filter function for the search
  const filteredData = data.filter((row) => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    // Search in all fields
    return (
      (row.date &&
        formatDate(row.date).toLowerCase().includes(searchLower)) ||
      (row.amount &&
        row.amount.toString().toLowerCase().includes(searchLower)) ||
      (row.transaction_id &&
        row.transaction_id.toLowerCase().includes(searchLower)) ||
      (row.txRef && row.txRef.toLowerCase().includes(searchLower)) ||
      (row.type && t(row.type).toLowerCase().includes(searchLower))
    );
  });

  const columns = [
    {
      title: t("requestDate"),
      field: "date",
      defaultSort: "desc",
      render: (rowData) => formatDate(rowData.date),
    },
    {
      title: t("amount"),
      field: "amount",
      editable: "never",
      render: (rowData) =>
        rowData.amount
          ? settings.swipe_symbol
            ? rowData.amount + " " + settings.symbol
            : settings.symbol + " " + rowData.amount
          : settings.swipe_symbol
          ? "0 " + settings.symbol
          : settings.symbol + " 0",
    },
    {
      title: t("transaction_id"),
      field: "transaction_id",
      render: (rowData) =>
        rowData.transaction_id ? rowData.transaction_id : rowData.txRef,
    },
    {
      title: t("type"),
      field: "type",
      headerStyle: {
        textAlign: "center",
      },
      cellStyle: {
        textAlign: "center",
      },
      render: (rowData) => (
        <div
          style={{
            backgroundColor:
              rowData.type === "debited"
                ? colors.RED
                : rowData.type === "credited"
                ? "#E8F5E9"
                : "#00B69B",
            color:
              rowData.type === "debited"
                ? "white"
                : rowData.type === "credited"
                ? "#388E3C"
                : "#ffffff",
            padding: "6px 16px",
            borderRadius: "100px",
            fontWeight: "500",
            width: "auto",
            minWidth: "80px",
            margin: "auto",
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {t(rowData.type)}
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Table title and search - outside the table */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: "16px" }}
      >
        <Grid item>
          <Typography
            variant="h5"
            style={{ fontWeight: 400, color: "#202224" }}
          >
            {t("transactionHistory")}
          </Typography>
        </Grid>
        <Grid item style={{ display: "flex", alignItems: "center" }}>
          <TextField
            placeholder={t("search")}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#9e9e9e" }} />
                </InputAdornment>
              ),
              style: {
                borderRadius: "100px",
                backgroundColor: "#fff",
                fontSize: "14px",
              },
            }}
            style={{ width: "240px" }}
          />
          {/* Export Button with Dropdown */}
          <IconButton
            size="small"
            style={{ marginLeft: "10px" }}
            onClick={handleExportClick}
            aria-controls={open ? "export-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <GetAppIcon style={{ color: "#757575" }} />
          </IconButton>

          {/* Export Menu */}
          <Menu
            id="export-menu"
            MenuListProps={{
              "aria-labelledby": "export-button",
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
            PaperProps={{
              style: {
                borderRadius: "8px",
                boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
                minWidth: "180px",
              },
            }}
          >
            <MenuItem onClick={() => exportData("default")}>
              <ListItemIcon>
                <FileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("export")} />
            </MenuItem>
            <MenuItem onClick={() => exportData("csv")}>
              <ListItemIcon>
                <CsvIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("export_as_csv")} />
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      {/* The table itself */}
      <ThemeProvider theme={theme}>
        <MaterialTable
          tableRef={tableRef}
          title=""
          columns={columns}
          data={filteredData}
          onRowClick={(evt, selectedRow) =>
            setSelectedRow(selectedRow?.tableData?.id)
          }
          options={{
            search: false,
            toolbar: false,
            exportButton: false,
            headerStyle: {
              backgroundColor: "white",
              color: "#202224",
              fontWeight: 800,
              borderBottom: "1px solid #e0e0e0",
              paddingTop: "16px",
              paddingBottom: "16px",
            },
            rowStyle: (rowData) => ({
              backgroundColor:
                selectedRow === rowData.tableData?.id ? "#f5f5f5" : "#fff",
              borderBottom: "1px solid #f0f0f0",
            }),
            pageSize: rowsPerPage,
            pageSizeOptions: [8, 16, 24, 50, 100],
            emptyRowsWhenPaging: false,
          }}
          onChangeRowsPerPage={(pageSize) => {
            setRowsPerPage(pageSize);
          }}
          onChangePage={(page) => {
            setPage(page);
          }}
          localization={localization(t)}
          style={{
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default TransactionTable;